import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Upload, Paperclip, FileText, Loader2 } from "lucide-react";

/**
 * Compact, embeddable uploader that posts to /api/documents/upload with a fixed category
 * so that documents are tagged in-context (e.g. "FINANCIAL" from CMA tab, "SALARY_SLIP"
 * from Personal Loan tab, "PROPERTY_DOC" from Housing tab, etc.).
 *
 * @param {string} applicationId
 * @param {string} category - DMS category key (must be one of DOC_CATEGORIES)
 * @param {string} [title]   - optional label
 * @param {string} [accept]  - file type filter (default "*")
 * @param {function} [onUploaded] - callback(doc) after each successful upload
 * @param {boolean} [multiple] - allow multi-select
 * @param {string} [tag] - optional tag stored with the doc
 */
export default function ContextualUploader({ applicationId, category, title, accept = "*", onUploaded, multiple = false, tag, compact = false }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [recent, setRecent] = useState([]);

  const handleFiles = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    const uploaded = [];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("application_id", applicationId);
        fd.append("category", category);
        if (tag) fd.append("tag", tag);
        fd.append("file", f);
        const r = await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data);
        if (onUploaded) onUploaded(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || `Upload failed: ${f.name}`);
      }
    }
    if (uploaded.length) {
      toast.success(`${uploaded.length} file(s) uploaded as ${category}`);
      setRecent((prev) => [...uploaded, ...prev].slice(0, 5));
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className={compact ? "" : "rounded-md border border-dashed border-border bg-muted/30 p-3"}>
      {!compact && (title || category) && (
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Paperclip className="h-3.5 w-3.5" />
            <span className="font-medium">{title || `Attach ${category.replace(/_/g, " ").toLowerCase()}`}</span>
            <Badge variant="outline" className="text-[10px]">{category}</Badge>
          </div>
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        <Button
          size="sm"
          variant="outline"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          data-testid={`contextual-upload-${category}`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Upload className="h-3.5 w-3.5 mr-1" />}
          {busy ? "Uploading…" : "Upload"}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={(e) => handleFiles(Array.from(e.target.files || []))}
          className="hidden"
        />
        {recent.length > 0 && (
          <span className="text-[11px] text-muted-foreground flex items-center gap-1">
            <FileText className="h-3 w-3" /> {recent.length} recently uploaded
          </span>
        )}
      </div>
    </div>
  );
}
